import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { useSiteContent } from "@/hooks/useSiteContent";

const Timeline = () => {
  const { t, isRTL, lang } = useLanguage();
  const headerRef = useReveal();
  const { data: doc } = useSiteContent("timeline");

  const heading = (lang === "ar" ? doc?.heading_ar : doc?.heading_en) || t.timeline.heading;
  const sub = (lang === "ar" ? doc?.sub_ar : doc?.sub_en) || t.timeline.sub;

  const items =
    Array.isArray(doc?.items) && doc.items.length
      ? doc.items.map((it) => ({
          year: it.year,
          tag: lang === "ar" ? it.tag_ar : it.tag_en,
          title: lang === "ar" ? it.title_ar : it.title_en,
          org: lang === "ar" ? it.org_ar : it.org_en,
        }))
      : t.timeline.items;

  return (
    <section id="timeline" data-testid="timeline-section" className="relative section-y bg-ink-soft border-t border-white/5">
      <div className="container-x">
        <div ref={headerRef} className="reveal max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline" />
            <span className="overline">{t.timeline.overline}</span>
          </div>
          <h2
            className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance`}
            data-testid="timeline-heading"
          >
            {heading}
          </h2>
          <p className={`mt-6 text-white/55 ${isRTL ? "font-arabic text-lg" : ""}`}>{sub}</p>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className={`absolute top-0 bottom-0 ${isRTL ? "right-4 sm:right-8" : "left-4 sm:left-8"} w-px bg-gradient-to-b from-transparent via-white/15 to-transparent`} />

          <ol className="space-y-14">
            {items.map((item, i) => (
              <TimelineItem key={i} item={item} index={i} isRTL={isRTL} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

const TimelineItem = ({ item, index, isRTL }) => {
  const ref = useReveal({ threshold: 0.2 });
  return (
    <li
      ref={ref}
      data-testid={`timeline-item-${index}`}
      className="reveal group relative pl-14 sm:pl-20 rtl:pl-0 rtl:pr-14 rtl:sm:pr-20"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Dot */}
      <span
        className={`absolute top-2 ${isRTL ? "right-3 sm:right-7" : "left-3 sm:left-7"} w-3 h-3 border border-gold bg-ink-soft group-hover:bg-gold transition-colors duration-500`}
      />
      {/* Connector line */}
      <span
        className={`absolute top-[0.85rem] ${isRTL ? "right-6 sm:right-10" : "left-6 sm:left-10"} w-8 sm:w-10 h-px bg-white/20 group-hover:bg-gold transition-colors duration-500`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-baseline">
        <div className="lg:col-span-3">
          <div className={`${isRTL ? "font-arabic font-black" : "font-serif"} text-gold text-5xl lg:text-6xl tracking-tight leading-none`}>
            {item.year}
          </div>
          <div className={`mt-3 overline ${isRTL ? "font-arabic tracking-normal text-sm" : ""}`}>{item.tag}</div>
        </div>
        <div className="lg:col-span-9">
          <h3
            className={`${isRTL ? "font-arabic font-bold" : "font-serif"} text-bone text-2xl sm:text-3xl lg:text-4xl leading-tight group-hover:text-gold transition-colors duration-500 text-balance`}
          >
            {item.title}
          </h3>
          <p className={`mt-3 text-white/55 ${isRTL ? "font-arabic text-lg" : "text-base"}`}>{item.org}</p>
        </div>
      </div>
    </li>
  );
};

export default Timeline;
